import { CheckIcon, PlusIcon } from '@heroicons/react/24/solid';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface StepCardProps {
  index?: number; // 1-based step number shown in the indicator (omit for optional add-ons)
  title: string;
  description: ReactNode;
  complete: boolean;
  isLast?: boolean;
  optional?: boolean; // renders a neutral "+" indicator instead of a number; not part of the numbered flow
  children?: ReactNode; // rich body content only (QR, badges, list) — no buttons
  actions?: ReactNode; // action row: DS Buttons, secondary…primary (rightmost)
}

/**
 * A single step in the Check In Check Out onboarding checklist.
 * Left: a numbered circle (or green check when complete) with a connecting rail.
 * Right: a card with the title, a Done pill, description, optional rich body, and a
 * consistent action row at the bottom. Actions stack full-width on mobile and sit
 * in a right-aligned row on larger screens.
 */
const StepCard = ({
  index,
  title,
  description,
  complete,
  isLast = false,
  optional = false,
  children,
  actions,
}: StepCardProps) => {
  const { t } = useTranslation();

  return (
    <li className="relative flex gap-3 sm:gap-4">
      {/* Indicator column + vertical rail */}
      <div className="flex flex-col items-center">
        <div
          className={[
            'flex h-8 w-8 flex-none items-center justify-center rounded-full text-sm font-semibold ring-1 sm:h-9 sm:w-9',
            complete
              ? 'bg-green-50 text-green-700 ring-green-400'
              : optional
                ? 'bg-gray-50 text-gray-500 ring-gray-300'
                : 'bg-blue-50 text-blue-700 ring-blue-300',
          ].join(' ')}
          aria-hidden="true"
        >
          {complete ? <CheckIcon className="h-5 w-5" /> : optional ? <PlusIcon className="h-5 w-5" /> : index}
        </div>
        {!isLast && <div className="my-1 w-px flex-1 bg-gray-200" />}
      </div>

      {/* Card */}
      <div
        className={`min-w-0 flex-1 rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-5 ${
          isLast ? '' : 'mb-4 sm:mb-6'
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            <p className="mt-1 text-sm leading-relaxed text-gray-600">{description}</p>
          </div>
          {complete && (
            <span className="inline-flex flex-none items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700 ring-1 ring-green-400">
              <CheckIcon className="h-3.5 w-3.5" />
              {t('common.done')}
            </span>
          )}
        </div>

        {children && <div className="mt-4">{children}</div>}

        {actions && (
          <div className="mt-5 flex flex-col-reverse items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
            {actions}
          </div>
        )}
      </div>
    </li>
  );
};

export default StepCard;
